import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Redirect } from "react-router-dom";
import { FaBolt, FaPaperPlane, FaTrash } from "react-icons/fa";
import SidebarPanelNavigation from "../../../../components/SidebarPanelNavigation";
import useAuth from "../../../../contexts/auth";
import {
  listWhatsAppWebSegments,
  promoteWhatsAppWebUrl,
  createWhatsAppWebCampaign,
} from "../../../../services/api";

/**
 * Multi Sender — cola lista de URLs (até 50), processa cada uma
 * via /promote (Shopee API → fallback Gemini), permite editar
 * texto/imagem e dispara N campanhas no segmento escolhido.
 */
export default function MultiSenderPage() {
  const { user } = useAuth();
  const [segments, setSegments] = useState([]);
  const [segmentId, setSegmentId] = useState("");
  const [linksText, setLinksText] = useState("");
  const [items, setItems] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [feedback, setFeedback] = useState(null);

  const [editing, setEditing] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    listWhatsAppWebSegments().then((res) =>
      setSegments(res?.data || [])
    );
  }, []);

  if (!user) return <Redirect to="/entrar" />;
  if (user.type !== "admin") return <Redirect to="/" />;

  const links = linksText
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.match(/^https?:\/\//));

  async function handleProcess() {
    if (links.length === 0) return alert("Cole ao menos uma URL.");
    if (links.length > 50) return alert("Máximo 50 URLs por batch.");

    setProcessing(true);
    setProgress({ done: 0, total: links.length });
    setFeedback(null);

    const results = [];
    for (const url of links) {
      try {
        const res = await promoteWhatsAppWebUrl(url);
        if (res?.success) {
          results.push({
            id: results.length + 1,
            ok: true,
            ...res.data,
            selected: true,
          });
        } else {
          results.push({ id: results.length + 1, ok: false, original_url: url });
        }
      } catch (err) {
        results.push({
          id: results.length + 1,
          ok: false,
          original_url: url,
          error: err.message,
        });
      }
      setProgress({ done: results.length, total: links.length });
      setItems([...results]);
    }
    setProcessing(false);
  }

  function toggleSelected(id) {
    setItems((curr) =>
      curr.map((it) => (it.id === id ? { ...it, selected: !it.selected } : it))
    );
  }

  function removeItem(id) {
    setItems((curr) => curr.filter((it) => it.id !== id));
  }

  function clearAll() {
    setItems([]);
    setLinksText("");
  }

  async function handleSend() {
    if (!segmentId) return alert("Selecione um segmento.");
    const selected = items.filter((it) => it.ok && it.selected);
    if (selected.length === 0) return alert("Selecione ao menos 1 oferta.");

    setSending(true);
    let ok = 0;
    let fail = 0;
    for (const it of selected) {
      try {
        const payload = {
          name: `Multi Campaign ${new Date().toLocaleString("pt-BR")} #${it.id}`,
          message_type: "image",
          message: it.promotion_text,
          media_path: it.image_url,
          media_mimetype: "image/jpeg",
          segment_id: segmentId,
        };
        await createWhatsAppWebCampaign(payload);
        ok++;
      } catch (err) {
        fail++;
      }
    }
    setSending(false);
    setFeedback({
      type: fail === 0 ? "success" : "warning",
      text: `✅ ${ok} campanha(s) criada(s)${
        fail > 0 ? `, ${fail} falha(s)` : ""
      }. Acompanhe em "Campanhas".`,
    });
    setItems([]);
    setLinksText("");
  }

  function saveEdit() {
    setItems((curr) =>
      curr.map((it) => (it.id === editing.id ? { ...it, ...editing } : it))
    );
    setEditing(null);
  }

  return (
    <Container fluid className="p-4">
      <Row>
        <Col md={3} lg={2}>
          <SidebarPanelNavigation isAdmin />
        </Col>
        <Col md={9} lg={10}>
          <h2 className="mb-1">⚡ Multi Sender</h2>
          <p className="text-muted">
            Cole até 50 URLs de produtos (Shopee, Amazon, Magalu, ML).
            O sistema converte links e gera campanhas em massa.
          </p>

          {feedback && (
            <Alert variant={feedback.type} dismissible onClose={() => setFeedback(null)}>
              {feedback.text}
            </Alert>
          )}

          <Row>
            {/* Coluna 1 — Configuração */}
            <Col md={5}>
              <Card>
                <Card.Body>
                  <Form.Group>
                    <Form.Label>Segmento alvo *</Form.Label>
                    <Form.Control
                      as="select"
                      value={segmentId}
                      onChange={(e) => setSegmentId(e.target.value)}
                    >
                      <option value="">Selecione...</option>
                      {segments.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({s.total_groups} grupos)
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Links das Promoções</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={12}
                      value={linksText}
                      onChange={(e) => setLinksText(e.target.value)}
                      placeholder={
                        "https://shopee.com.br/produto-x\nhttps://amazon.com.br/dp/B0...\nhttps://magazineluiza.com.br/...\nhttps://produto.mercadolivre.com.br/..."
                      }
                    />
                    <Form.Text className="text-muted">
                      {links.length}/50 link(s)
                    </Form.Text>
                  </Form.Group>

                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={handleProcess}
                    disabled={processing || links.length === 0}
                  >
                    {processing ? (
                      <>
                        <Spinner size="sm" animation="border" /> Processando{" "}
                        {progress.done}/{progress.total}...
                      </>
                    ) : (
                      <>
                        <FaBolt /> Processar Links
                      </>
                    )}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Coluna 2 — Resultados */}
            <Col md={7}>
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">
                      📋 Promoções Processadas
                      {items.length > 0 && (
                        <Badge variant="primary" className="ml-2">
                          {items.length}
                        </Badge>
                      )}
                    </h5>
                    {items.length > 0 && (
                      <div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={clearAll}
                        >
                          Limpar tudo
                        </Button>
                      </div>
                    )}
                  </div>

                  {items.length === 0 ? (
                    <div className="text-center p-4 text-muted">
                      Cole os links no painel ao lado e clique em "Processar Links".
                    </div>
                  ) : (
                    <>
                      <Table size="sm" responsive striped>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th></th>
                            <th>IMG</th>
                            <th>Produto</th>
                            <th>Mensagem</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((it) => (
                            <tr key={it.id}>
                              <td>{it.id}</td>
                              <td>
                                {it.ok ? (
                                  <Form.Check
                                    type="checkbox"
                                    checked={it.selected}
                                    onChange={() => toggleSelected(it.id)}
                                  />
                                ) : (
                                  <Badge variant="danger">Erro</Badge>
                                )}
                              </td>
                              <td>
                                {it.image_url ? (
                                  <img
                                    src={it.image_url}
                                    alt=""
                                    style={{
                                      width: 40,
                                      height: 40,
                                      objectFit: "cover",
                                      borderRadius: 4,
                                    }}
                                  />
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td style={{ fontSize: 12 }}>
                                {it.scraped_data?.title ||
                                  it.original_url?.substring(0, 50)}
                              </td>
                              <td style={{ fontSize: 11, maxWidth: 200 }}>
                                {(it.promotion_text || "").substring(0, 80)}…
                              </td>
                              <td>
                                {it.ok && (
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => setEditing(it)}
                                  >
                                    Detalhes
                                  </Button>
                                )}{" "}
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => removeItem(it.id)}
                                >
                                  <FaTrash />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>

                      <Button
                        variant="primary"
                        className="w-100 mt-2"
                        onClick={handleSend}
                        disabled={sending}
                      >
                        {sending ? (
                          <Spinner size="sm" animation="border" />
                        ) : (
                          <FaPaperPlane />
                        )}{" "}
                        Enviar {items.filter((i) => i.ok && i.selected).length}{" "}
                        Campanhas
                      </Button>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Modal Detalhes da Promoção */}
      <Modal show={!!editing} onHide={() => setEditing(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalhes da Promoção</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editing && (
            <>
              <Form.Group>
                <Form.Label>Imagem</Form.Label>
                {editing.image_url && (
                  <img
                    src={editing.image_url}
                    alt=""
                    style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8 }}
                  />
                )}
              </Form.Group>
              <Form.Group>
                <Form.Label>Texto da Mensagem</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={10}
                  value={editing.promotion_text || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, promotion_text: e.target.value })
                  }
                />
                <Form.Text className="text-muted">
                  Use <code>*negrito*</code>, <code>_itálico_</code>,{" "}
                  <code>~tachado~</code>
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditing(null)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={saveEdit}>
            Salvar Alterações
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
